import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Collapse,
  Divider,
  Select,
  MenuItem,
  Link,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import PhoneIcon from '@mui/icons-material/Phone';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import type { DisplayLogEntry } from '../utils/mergeLogsWithComments';
import { splitLogText } from '../utils/logTextLinks';
import type { JobLogType } from '@techgitter/contracts';

interface JobLogPanelProps {
  entries: DisplayLogEntry[];
  isLoading: boolean;
  error: string | null;
  isSubmitting: boolean;
  onAddLog: (req: { type: JobLogType; text: string }) => Promise<void>;
  onSync?: () => Promise<void>;
  isSyncing?: boolean;
  customerFirstName: string;
  userFirstName: string;
  customerPhone?: string;
  /** Bumped by the parent to focus the input and preselect the Diagnostics
   *  comment type (e.g. after declining the no-diagnostics quote prompt). */
  focusDiagnosticsSignal?: number;
}

function formatLogDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateStr;
  }
}

const CALL_ATTEMPT_OPTIONS = [
  'Left vm for return call',
  'Could not leave a vm',
  'Customers phone not in service',
] as const;

export const JobLogPanel: React.FC<JobLogPanelProps> = ({
  entries,
  isLoading,
  error,
  isSubmitting,
  onAddLog,
  onSync,
  isSyncing = false,
  customerFirstName,
  userFirstName,
  customerPhone,
  focusDiagnosticsSignal,
}) => {
  const [smsMessage, setSmsMessage] = useState<string | null>(null);
  const [newText, setNewText] = useState('');
  const [logType, setLogType] = useState<JobLogType>('comment');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSave = async () => {
    const text = newText.trim();
    if (!text) return;
    await onAddLog({ type: logType, text });
    setNewText('');
  };

  const handleLogTypeChange = async (e: SelectChangeEvent<string>) => {
    const type = e.target.value as JobLogType;
    setLogType(type);
    const text = newText.trim();
    if (!text) return;
    await onAddLog({ type, text });
    setNewText('');
  };

  const handleCallAttempt = async (e: SelectChangeEvent<string>) => {
    const option = e.target.value;
    if (!option) return;
    await onAddLog({ type: 'contact', text: option });

    if (option === 'Customers phone not in service') {
      const phone = customerPhone ? customerPhone.replace(/\s/g, '') : '<phone>';
      setSmsMessage(
        `Please send the following email from the service mailbox to ${phone}@sms.clicksend.com :\n\n` +
        `Hi ${customerFirstName}, ${userFirstName} @TechSolvers here, we tried calling but couldn't reach you, please call us on 02 8502 8954 for an update on your job.`,
      );
    }
  };

  const [logsExpanded, setLogsExpanded] = useState(true);
  const [olderExpanded, setOlderExpanded] = useState(false);

  // When the parent bumps the signal (e.g. the tech declined the
  // no-diagnostics quote prompt), reveal the input, switch the comment type
  // to Diagnostics, and focus the field so they can post one straight away.
  const lastFocusDiagnosticsSignal = useRef(focusDiagnosticsSignal);
  useEffect(() => {
    if (
      focusDiagnosticsSignal === undefined ||
      focusDiagnosticsSignal === lastFocusDiagnosticsSignal.current
    ) {
      return;
    }
    lastFocusDiagnosticsSignal.current = focusDiagnosticsSignal;
    setLogType('diagnostics');
    setLogsExpanded(true);
    inputRef.current?.focus();
  }, [focusDiagnosticsSignal]);

  const renderEntry = (entry: DisplayLogEntry, _index: number) => {
    const createdBy = entry.kind === 'log' ? entry.log.createdBy : entry.createdBy;
    const createdAt = entry.kind === 'log' ? entry.log.createdAt : entry.createdAt;
    const text = entry.kind === 'log' ? entry.log.text : entry.text;

    return (
      <Box sx={{ py: 1.5, px: 1 }}>
        {/* Entry header: Author | Type | Date */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {createdBy && (
              <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {createdBy}
              </Typography>
            )}
            {entry.kind === 'log' ? (
              <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em' }}>
                {entry.log.type === 'comment' ? 'COMMENT' : entry.log.type === 'standup' ? 'STANDUP' : entry.log.type === 'contact' ? 'OUTGOING CALL ATTEMPT' : entry.log.type === 'diagnostics' ? 'DIAGNOSTICS' : (entry.log.type ?? 'LOG').toUpperCase()}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.04em', color: '#1565c0' }}>
                RS
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {createdAt && (
              <Typography variant="caption" color="text.secondary">
                {formatLogDate(createdAt)}
              </Typography>
            )}
          </Box>
        </Box>
        {/* Entry body */}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {entry.kind === 'comment' && entry.subject && (
            <Box component="span" sx={{ fontWeight: 600 }}>{entry.subject}: </Box>
          )}
          {splitLogText(text).map((segment, i) =>
            segment.kind === 'link' ? (
              <Link key={i} href={segment.href} target="_blank" rel="noopener noreferrer">
                {segment.label}
              </Link>
            ) : (
              <React.Fragment key={i}>{segment.value}</React.Fragment>
            ),
          )}
        </Typography>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Section header — "N Communications" with collapse toggle */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', mb: 1 }}
        onClick={() => setLogsExpanded(prev => !prev)}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Job Log ({entries.length} {entries.length === 1 ? 'entry' : 'entries'})
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            color: 'text.secondary',
            transform: logsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Box>

      <Collapse in={logsExpanded}>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {/* Input area — always visible */}
        <Box sx={{ mb: 2 }}>
          <TextField
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            size="small"
            placeholder="What's next for this job?"
            value={newText}
            onChange={e => setNewText(e.target.value)}
            inputRef={inputRef}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end' }}>
            <Select
              size="small"
              value=""
              displayEmpty
              onChange={handleCallAttempt}
              disabled={isSubmitting}
              inputProps={{ tabIndex: -1 }}
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon sx={{ fontSize: 16 }} />
                  Log call
                </Box>
              )}
              sx={{ color: 'primary.main', height: 32, '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' } }}
            >
              {CALL_ATTEMPT_OPTIONS.map(option => (
                <MenuItem key={option} value={option} sx={{ fontSize: '0.8rem' }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            <Select
              size="small"
              value={logType}
              onChange={handleLogTypeChange}
              disabled={isSubmitting}
              sx={{ color: 'primary.main', height: 32, '& .MuiSelect-select': { py: 0.5, fontSize: '0.8rem' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' } }}
            >
              <MenuItem value="comment" sx={{ fontSize: '0.8rem' }}>Comment</MenuItem>
              <MenuItem value="standup" sx={{ fontSize: '0.8rem' }}>Standup</MenuItem>
              <MenuItem value="diagnostics" sx={{ fontSize: '0.8rem' }}>Diagnostics</MenuItem>
            </Select>
            {onSync && (
              <IconButton
                size="small"
                onClick={onSync}
                disabled={isSyncing}
                sx={{ color: 'primary.main' }}
              >
                {isSyncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
              </IconButton>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={!newText.trim() || isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>

        {/* SMS message */}
        {smsMessage && (
          <Alert severity="info" sx={{ mb: 1 }} onClose={() => setSmsMessage(null)}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {smsMessage}
            </Typography>
          </Alert>
        )}

        {/* Log entries — newest first, older entries collapsed */}
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No log entries yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {renderEntry(entries[0], 0)}
            {entries.length > 1 && (
              <>
                <Box
                  onClick={() => setOlderExpanded(prev => !prev)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    py: 0.5,
                    gap: 1,
                    '&:hover .expand-line': { borderColor: 'text.secondary' },
                  }}
                >
                  <Divider className="expand-line" sx={{ flex: 1, transition: 'border-color 0.2s ease' }} />
                  <KeyboardDoubleArrowDownIcon
                    sx={{
                      fontSize: 16,
                      color: 'text.disabled',
                      transform: olderExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                    }}
                  />
                  <Divider className="expand-line" sx={{ flex: 1, transition: 'border-color 0.2s ease' }} />
                </Box>
                <Collapse in={olderExpanded}>
                  {entries.slice(1).map((entry, i) => (
                    <React.Fragment key={entry.kind === 'log' ? entry.log.guid : `comment-${entry.id}`}>
                      {i > 0 && <Divider />}
                      {renderEntry(entry, i + 1)}
                    </React.Fragment>
                  ))}
                </Collapse>
              </>
            )}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};
